import { createDirectus, rest, staticToken, readItems, withOptions, type Query } from '@directus/sdk';
import { cache } from 'react';
import { formatDirectusFetchFailure } from '@/lib/directusHealth';
import { SITE_URL } from '@/lib/seo';

/** Narrow catch values that may carry an HTTP Response (Directus SDK errors). */
type ErrorWithResponse = {
  response?: { status?: number };
  message?: string;
};

// ---------------------------------------------------------------------------
// Next.js cache tags
// ---------------------------------------------------------------------------
// Every Directus fetch we perform is tagged so that `/api/revalidate` can
// invalidate the relevant cache entries on demand (driven by a Directus
// Flow/Webhook). Tags are intentionally coarse-grained at the collection
// level, plus a per-article tag so we can target individual posts when only
// one article changes.
export const CACHE_TAGS = {
  articles: 'articles',
  pages: 'pages',
  files: 'files',
  /** Per-article tag, derived from the article slug. */
  article: (slug: string) => `article:${slug.replace(/\/$/, '')}`,
} as const;

// Define the Directus schema
interface DirectusSchema {
  posts: Article[];
  pages: Page[];
}

// Article interface matching Directus collection
export interface Article {
  id: number;
  status: string;
  site: string; // Site relation UUID
  date_created: string;
  date_updated: string;
  publish_date?: string; // Correct field name in Directus
  title: string;
  slug: string;
  category: string;
  seo_title?: string;
  seo_description?: string;
  seo_focus_keyphrase?: string;
  featured_image?: number | DirectusFile; // Can be ID or populated file object
  content: string; // HTML or rich text content
}

export interface DirectusFile {
  id: string;
  filename_download: string;
  title?: string;
  description?: string;
  width?: number;
  height?: number;
  type?: string;
}

// Page interface matching Directus pages collection
export interface Page {
  id: number;
  status: string;
  site: number;
  title: string;
  slug: string;
  content?: string;
  seo_title?: string;
  seo_description?: string;
  seo_keywords?: string[];
  card_image?: number | DirectusFile; // Can be ID or populated file object
  date_created?: string;
  date_updated?: string;
}

type ArticlesQuery = Query<DirectusSchema, Article>;
type PagesQuery = Query<DirectusSchema, Page>;

/** Mutable list-query builder; cast to ArticlesQuery/PagesQuery at SDK call sites. */
type ListQueryBuilder = {
  fields?: string[];
  filter: {
    status: { _eq: string };
    category?: { _eq: string };
    slug?: { _eq: string };
  };
  sort?: string[];
  limit?: number;
  offset?: number;
};

/**
 * Normalise environment variables that point at a hostname. Trailing
 * whitespace (newlines in particular) silently propagates into every
 * constructed URL — and because the public NEXT_PUBLIC_DIRECTUS_URL is
 * inlined at build time into the client bundle, the newline ends up
 * baked into every <Image src=…> URL. Next.js's image optimizer then
 * rejects those URLs with `INVALID_IMAGE_OPTIMIZE_REQUEST` (HTTP 400),
 * forcing a fallback to the raw Directus CDN and wrecking LCP.
 */
function normaliseUrl(value: string | undefined): string | undefined {
  if (!value) return value;
  return value.trim().replace(/\/$/, '');
}

const directusUrl = normaliseUrl(
  process.env.NEXT_PUBLIC_DIRECTUS_URL || process.env.DIRECTUS_URL
);
const directusToken = process.env.DIRECTUS_TOKEN?.trim();
const originAuthSecret = process.env.ORIGIN_AUTH_SECRET?.trim();

if (!directusUrl) {
  console.warn('DIRECTUS_URL or NEXT_PUBLIC_DIRECTUS_URL environment variable is not set. CMS features will be unavailable.');
}

// Create the Directus client when URL and token are available.
// X-Origin-Auth is sent when ORIGIN_AUTH_SECRET is set (required from Vercel/nginx);
// local dev can reach Directus with Bearer token alone from allowlisted IPs.
export const directus = directusUrl && directusToken
  ? createDirectus<DirectusSchema>(directusUrl)
      .with(
        rest({
          onRequest: (options) => {
            if (!originAuthSecret) return options;
            const headers = new Headers(options.headers);
            headers.set('X-Origin-Auth', originAuthSecret);
            return { ...options, headers };
          },
        })
      )
      .with(staticToken(directusToken))
  : null;

/**
 * Wraps a Directus REST command with Next.js cache tags so that pages
 * relying on that data can be revalidated on demand via `revalidateTag`.
 *
 * Next.js extends the standard `RequestInit` with a `next` field — we cast
 * because `@directus/sdk` types this as plain `RequestInit`. See:
 * https://nextjs.org/docs/app/api-reference/functions/revalidateTag
 *
 * We deliberately return the command as-is type-wise: `withOptions` strips
 * generics but its runtime behaviour is purely additive (it only sets
 * `onRequest`), so callers continue to see the original `RestCommand` output
 * type.
 */
function withTags<T>(command: T, tags: string[]): T {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return withOptions(command as any, { next: { tags } } as any) as T;
}

function logDirectusError(context: string, error: unknown): void {
  console.error(formatDirectusFetchFailure(context, error));
}

/**
 * Retries an async operation with exponential backoff.
 * Only retries on retryable errors (network, socket, 5xx, timeout).
 * Does NOT retry on auth failures (401) or not-found (404).
 */
async function withRetry<T>(
  operation: () => Promise<T>,
  { retries = 3, baseDelayMs = 300 }: { retries?: number; baseDelayMs?: number } = {}
): Promise<T> {
  let lastError: unknown;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error;

      // Don't retry on the last attempt
      if (attempt === retries) break;

      // Determine if error is retryable
      const isRetryable = (() => {
        // Network / socket / fetch abort errors
        if (error instanceof TypeError) return true;
        if (error && typeof error === 'object') {
          const msg = String((error as Error).message || '').toLowerCase();
          if (
            msg.includes('socket') ||
            msg.includes('network') ||
            msg.includes('fetch') ||
            msg.includes('terminated') ||
            msg.includes('timeout') ||
            msg.includes('econnreset') ||
            msg.includes('etimedout')
          ) {
            return true;
          }
          // Check for HTTP status in Directus SDK errors
          const response = (error as ErrorWithResponse).response;
          if (response?.status !== undefined && response.status >= 500) return true;
          if (response?.status === 429) return true;
        }
        return false;
      })();

      if (!isRetryable) throw error;

      const delay = baseDelayMs * Math.pow(2, attempt);
      console.warn(`Directus request failed (attempt ${attempt + 1}/${retries + 1}), retrying in ${delay}ms...`);
      await new Promise((r) => setTimeout(r, delay));
    }
  }
  throw lastError;
}

function getAssetId(fileId: string | number | DirectusFile): string {
  if (!fileId) return '';

  if (typeof fileId === 'object' && 'id' in fileId) {
    return String(fileId.id);
  }

  return String(fileId);
}

// Helper function to get the public asset URL routed through this app.
export interface AssetTransformOptions {
  width?: number;
  height?: number;
  fit?: "cover" | "contain" | "inside" | "outside";
  quality?: number;
  format?: "jpg" | "jpeg" | "png" | "webp" | "tiff";
  withoutEnlargement?: boolean;
}

/** 96px display at 2x DPR — sharp list thumbnails without full originals. */
export const LIST_THUMBNAIL_TRANSFORM: AssetTransformOptions = {
  width: 192,
  height: 192,
  fit: "cover",
  quality: 80,
  withoutEnlargement: true,
};

/** Hero sidebar thumbs stretch tall (~96×140px) — request extra height at 2x. */
export const HERO_SIDEBAR_THUMBNAIL_TRANSFORM: AssetTransformOptions = {
  width: 192,
  height: 320,
  fit: "cover",
  quality: 80,
  withoutEnlargement: true,
};

export function getAssetUrl(
  fileId: string | number | DirectusFile,
  transform?: AssetTransformOptions
): string {
  const assetId = getAssetId(fileId);

  if (!assetId) {
    return "";
  }

  const base = `/api/directus-assets/${encodeURIComponent(assetId)}`;
  if (!transform) {
    return base;
  }

  const params = new URLSearchParams();
  if (transform.width) params.set("width", String(transform.width));
  if (transform.height) params.set("height", String(transform.height));
  if (transform.fit) params.set("fit", transform.fit);
  if (transform.quality) params.set("quality", String(transform.quality));
  if (transform.format) params.set("format", transform.format);
  if (transform.withoutEnlargement) {
    params.set("withoutEnlargement", "true");
  }

  const query = params.toString();
  return query ? `${base}?${query}` : base;
}

export function getAbsoluteAssetUrl(
  fileId: string | number | DirectusFile,
  transform?: AssetTransformOptions
): string {
  const path = getAssetUrl(fileId, transform);

  return path ? `${SITE_URL}${path}` : "";
}

/**
 * Fetches file metadata from Directus when featured_image is just an ID
 * Returns the file object with metadata, or null if not found
 */
export async function getFileMetadata(fileId: number | string): Promise<DirectusFile | null> {
  try {
    if (!directusUrl || !directusToken) {
      return null;
    }

    const fileHeaders: Record<string, string> = {
      Authorization: `Bearer ${directusToken}`,
    };
    if (originAuthSecret) {
      fileHeaders['X-Origin-Auth'] = originAuthSecret;
    }

    // Use Directus REST API to fetch file metadata. The Next.js fetch
    // extension tags the response so it participates in on-demand
    // revalidation alongside SDK-driven calls.
    const response = await withRetry(() =>
      fetch(`${directusUrl}/files/${fileId}`, {
        headers: fileHeaders,
        next: { tags: [CACHE_TAGS.files] },
      } as RequestInit)
    );

    if (!response.ok) {
      return null;
    }

    const file = await response.json();
    return file.data as DirectusFile;
  } catch (error) {
    logDirectusError('file metadata', error);
    return null;
  }
}

/**
 * Normalizes featured_image to always return a DirectusFile object or null
 * Fetches file metadata if featured_image is just an ID
 */
export async function normalizeFeaturedImage(
  featuredImage: number | DirectusFile | undefined
): Promise<DirectusFile | null> {
  if (!featuredImage) {
    return null;
  }

  // If it's already a file object, return it
  if (typeof featuredImage === 'object' && 'id' in featuredImage) {
    return featuredImage;
  }

  // If it's just an ID, fetch the file metadata
  if (typeof featuredImage === 'number' || typeof featuredImage === 'string') {
    return await getFileMetadata(featuredImage);
  }

  return null;
}

// Pagination result interface
export interface PaginatedArticles {
  articles: Article[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface SitemapArticle {
  slug: string;
  title?: string;
  category?: string;
  seo_description?: string;
  publish_date?: string;
  date_updated: string;
  date_created: string;
  featured_image?: number | DirectusFile;
}

// Fetch all articles - function overloads for better type safety
export async function getArticles(options?: {
  limit?: number;
  offset?: number;
  category?: string;
  sort?: string[];
  includeTotal?: false;
}): Promise<Article[]>;
export async function getArticles(options: {
  limit?: number;
  offset?: number;
  category?: string;
  sort?: string[];
  includeTotal: true;
}): Promise<PaginatedArticles>;
export async function getArticles(options?: {
  limit?: number;
  offset?: number;
  category?: string;
  sort?: string[];
  includeTotal?: boolean;
}): Promise<Article[] | PaginatedArticles> {
  try {
    if (!directus) {
      console.warn('Directus client not initialized. Returning empty results.');
      if (options?.includeTotal) {
        return { articles: [], total: 0, page: 1, pageSize: options.limit || 12, totalPages: 0 };
      }
      return [];
    }

    const query: ListQueryBuilder = {
      fields: ['*', 'featured_image.*'],
      filter: {
        status: { _eq: 'published' }
      }
    };

    if (options?.limit) {
      query.limit = options.limit;
    }

    if (options?.offset !== undefined) {
      query.offset = options.offset;
    }

    if (options?.category) {
      query.filter.category = { _eq: options.category };
    }

    if (options?.sort) {
      query.sort = options.sort;
    } else {
      // Sort by publish_date (newest published first), with date_updated as fallback
      // This ensures articles display in proper chronological order based on when they were published
      query.sort = ['-publish_date', '-date_updated'];
    }

    // If pagination is requested, get total count
    if (options?.includeTotal) {
      // Get total count with a separate minimal query
      const countQuery: ListQueryBuilder = {
        filter: {
          status: { _eq: 'published' }
        }
      };

      if (options?.category) {
        countQuery.filter.category = { _eq: options.category };
      }

      // Fetch all matching IDs to count (using a high limit for practical purposes)
      // For blogs with >10k articles, consider using Directus aggregation API
      const allItems = await withRetry(() =>
        directus.request(
          withTags(
            readItems<DirectusSchema, 'posts', ArticlesQuery>('posts', {
              ...countQuery,
              fields: ['id'],
              limit: 10000, // Practical limit for counting
            } as ArticlesQuery),
            [CACHE_TAGS.articles]
          )
        )
      ) as unknown as { id: number }[];
      
      const total = Array.isArray(allItems) ? allItems.length : 0;
      
      // Then get the paginated articles
      const articles = await withRetry(() =>
        directus.request(
          withTags(
            readItems<DirectusSchema, 'posts', ArticlesQuery>('posts', query as ArticlesQuery),
            [CACHE_TAGS.articles]
          )
        )
      ) as unknown as Article[];

      const pageSize = options.limit || 12;
      const page = options.offset ? Math.floor(options.offset / pageSize) + 1 : 1;
      const totalPages = Math.ceil(total / pageSize);

      return {
        articles,
        total,
        page,
        pageSize,
        totalPages,
      };
    }

    const articles = await withRetry(() =>
      directus.request(
        withTags(
          readItems<DirectusSchema, 'posts', ArticlesQuery>('posts', query as ArticlesQuery),
          [CACHE_TAGS.articles]
        )
      )
    );

    return articles as unknown as Article[];
  } catch (error) {
    logDirectusError('articles', error);
    // Return empty results instead of throwing to prevent page crashes
    if (options?.includeTotal) {
      return { articles: [], total: 0, page: 1, pageSize: options.limit || 12, totalPages: 0 };
    }
    return [];
  }
}

// Fetch a single article by slug
export const getArticleBySlug = cache(async function getArticleBySlug(
  slug: string
): Promise<Article | null> {
  const normalizedSlug = slug.replace(/\/$/, '');

  try {
    if (!directus) {
      console.warn('Directus client not initialized. Cannot fetch article.');
      return null;
    }

    const articles = await withRetry(() =>
      directus.request(
        withTags(
          readItems<DirectusSchema, 'posts', ArticlesQuery>('posts', {
            fields: ['*', 'featured_image.*'],
            filter: {
              slug: { _eq: normalizedSlug },
              status: { _eq: 'published' }
            },
            limit: 1
          } as ArticlesQuery),
          // Tag with both the collection-wide tag and a per-slug tag so a
          // webhook can target a single article precisely.
          [CACHE_TAGS.articles, CACHE_TAGS.article(normalizedSlug)]
        )
      )
    );

    if (!articles || articles.length === 0) {
      return null;
    }

    return articles[0] as unknown as Article;
  } catch (error) {
    logDirectusError(`article slug "${normalizedSlug}"`, error);
    return null;
  }
});

// Fetch all article slugs (for static generation)
export async function getAllArticleSlugs(): Promise<string[]> {
  try {
    if (!directus) {
      return [];
    }

    const articles = await withRetry(() =>
      directus.request(
        withTags(
          readItems<DirectusSchema, 'posts', ArticlesQuery>('posts', {
            fields: ['slug'],
            filter: {
              status: { _eq: 'published' }
            }
          } as ArticlesQuery),
          [CACHE_TAGS.articles]
        )
      )
    );

    return (articles as unknown as Array<{ slug: string }>).map((article) => article.slug);
  } catch (error) {
    logDirectusError('article slugs', error);
    return [];
  }
}

/**
 * Category counts for the filter pills on listing pages.
 *
 * Fetching the whole article list just to count categories sends the entire
 * CMS content (~5 MB on this site) over the wire, blows up the RSC payload
 * and hurts FCP/LCP dramatically. This helper fetches only the `category`
 * column — tens of KB instead of megabytes.
 */
export interface CategoryCounts {
  All: number;
  Training: number;
  Nutrition: number;
  Lifestyle: number;
}

export async function getArticleCategoryCounts(): Promise<CategoryCounts> {
  const empty: CategoryCounts = { All: 0, Training: 0, Nutrition: 0, Lifestyle: 0 };

  try {
    if (!directus) {
      return empty;
    }

    const rows = (await withRetry(() =>
      directus.request(
        withTags(
          readItems<DirectusSchema, 'posts', ArticlesQuery>('posts', {
            fields: ['category'],
            filter: { status: { _eq: 'published' } },
            limit: -1,
          } as ArticlesQuery),
          [CACHE_TAGS.articles]
        )
      )
    )) as unknown as Array<{ category?: string | null }>;

    const counts: CategoryCounts = { ...empty };
    for (const row of rows) {
      counts.All += 1;
      if (row.category === 'Training') counts.Training += 1;
      else if (row.category === 'Nutrition') counts.Nutrition += 1;
      else if (row.category === 'Lifestyle') counts.Lifestyle += 1;
    }
    return counts;
  } catch (error) {
    logDirectusError('article category counts', error);
    return empty;
  }
}

// Fetch lightweight article data for sitemap generation
export async function getSitemapArticles(): Promise<SitemapArticle[]> {
  try {
    if (!directus) {
      console.warn('Directus client not initialized. Cannot fetch sitemap articles.');
      return [];
    }

    const pageSize = 500;
    const sitemapArticles: SitemapArticle[] = [];
    let offset = 0;

    while (true) {
      const batch = await withRetry(() =>
        directus.request(
          withTags(
            readItems<DirectusSchema, 'posts', ArticlesQuery>('posts', {
              fields: ['slug', 'title', 'category', 'seo_description', 'publish_date', 'date_updated', 'date_created', 'featured_image.*'],
              filter: {
                status: { _eq: 'published' }
              },
              sort: ['-publish_date', '-date_updated'],
              limit: pageSize,
              offset,
            } as ArticlesQuery),
            [CACHE_TAGS.articles]
          )
        )
      ) as unknown as SitemapArticle[];

      if (!Array.isArray(batch) || batch.length === 0) {
        break;
      }

      sitemapArticles.push(...batch);

      if (batch.length < pageSize) {
        break;
      }

      offset += pageSize;
    }

    return sitemapArticles;
  } catch (error) {
    logDirectusError('sitemap articles', error);
    return [];
  }
}

// Cache configuration helper
export const revalidateConfig = {
  revalidate: 60 // Revalidate every 60 seconds (same as Contentful setup)
};

// Fetch a page by slug
export const getPageBySlug = cache(async function getPageBySlug(
  slug: string
): Promise<Page | null> {
  const normalizedSlug = slug.replace(/\/$/, '');

  try {
    if (!directus) {
      return null;
    }

    const pages = await withRetry(() =>
      directus.request(
        withTags(
          readItems<DirectusSchema, 'pages', PagesQuery>('pages', {
            fields: ['*', 'card_image.*'],
            filter: {
              slug: { _eq: normalizedSlug },
              status: { _eq: 'published' }
            },
            limit: 1
          } as PagesQuery),
          [CACHE_TAGS.pages]
        )
      )
    );

    if (!pages || pages.length === 0) {
      return null;
    }

    return pages[0] as unknown as Page;
  } catch (error) {
    logDirectusError(`page slug "${normalizedSlug}"`, error);
    return null;
  }
});
